import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, FileSignature, Scale, MessageSquare, ShieldAlert, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import { buildWhatsAppLink, getPageWaLink } from "../data/firm";

/**
 * Landing page for the AI Legal Assistant suite. We surface the
 * three existing AI products (Sami chat, Document Review, Case
 * Assessment) as three premium-looking entry points — each links to
 * the existing, protected route.
 */
export default function AILegalAssistant() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const { user } = useAuth();
  const needLogin = !user;
  const waAi = getPageWaLink("ai", lang);

  const tools = [
    {
      icon: MessageSquare,
      to: needLogin ? "/login?next=%2Fconsultant" : "/consultant",
      titleAr: "اسأل سامي", titleEn: "Ask Sami",
      bodyAr: "محادثة فورية. اسأل أي سؤال قانوني واحصل على إجابة مدعومة بمراجع من القانون الكويتي.",
      bodyEn: "Instant chat. Ask any legal question and get an answer grounded in Kuwaiti law references.",
      testid: "ai-tool-consultant",
    },
    {
      icon: FileText,
      to: needLogin ? "/login?next=%2Fdocument-review" : "/document-review",
      titleAr: "مراجعة المستندات", titleEn: "Document Review",
      bodyAr: "ارفع عقداً أو مستنداً واحصل على ملخص، تحديد للمشاكل، والمستندات الناقصة.",
      bodyEn: "Upload a contract or document and get a summary, issue spotting, and missing-evidence list.",
      testid: "ai-tool-doc-review",
    },
    {
      icon: Scale,
      to: needLogin ? "/login?next=%2Fcase-assessment" : "/case-assessment",
      titleAr: "تقييم القضية", titleEn: "Case Assessment",
      bodyAr: "تحليل قانوني كامل: المستوى التقديري للمخاطر، الخطوات الموصى بها، ونقاط القوة والضعف.",
      bodyEn: "Full legal analysis: risk level, recommended steps, prospects of success.",
      testid: "ai-tool-case-assessment",
    },
    {
      icon: FileSignature,
      to: needLogin ? "/login?next=%2Fcontracts" : "/contracts",
      titleAr: "صياغة العقود", titleEn: "Contract Drafting",
      bodyAr: "أنشئ عقوداً قانونية احترافية فوراً — NDA، عمل، خدمات، إيجار، شراكة وغيرها — مُصاغة وفق القانون الكويتي.",
      bodyEn: "Generate professional legal contracts instantly — NDA, employment, services, lease, partnership and more — drafted to Kuwait law.",
      testid: "ai-tool-contracts",
    },
  ];

  return (
    <div data-testid="ai-legal-assistant-page">
      <SEO
        path="/ai-legal-assistant"
        titleAr="الذكاء القانوني | اسأل سامي، حلّل مستنداتك، قيّم قضيتك، صُغْ عقودك"
        titleEn="AI Legal Suite | Ask Sami, Review Docs, Assess Cases, Draft Contracts"
        descriptionAr="أربع أدوات ذكاء قانوني: محادثة سامي، مراجعة المستندات، تقييم القضايا، وصياغة العقود — مدعومة بمكتبة قوانين الكويت."
        descriptionEn="Four AI legal tools — Sami chat, document review, case assessment, and contract drafting. Powered by Kuwait's complete legal library."
        jsonLd={[orgJsonLd(), breadcrumbJsonLd([
          { name: isAr ? "الرئيسية" : "Home", path: "/" },
          { name: isAr ? "الذكاء القانوني" : "AI Assistant", path: "/ai-legal-assistant" },
        ])]}
      />

      {/* HERO */}
      <section className="section-y relative overflow-hidden" style={{ background: "var(--navy-deep)", color: "#fff" }}>
        <div className="absolute inset-0">
          <div className="absolute -top-32 -end-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-20" style={{ background: "var(--gold)" }} />
          <div className="absolute -bottom-32 -start-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-15" style={{ background: "var(--gold)" }} />
        </div>
        <div className="container-luxe relative text-center max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full mb-6"
               style={{ background: "rgba(201,169,97,0.15)", color: "var(--gold-soft)" }}>
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-xs font-medium tracking-wider uppercase">{isAr ? "ذكاء قانوني" : "AI Legal"}</span>
          </div>
          <h1 className={`${isAr ? "font-arabic" : "font-display"}`}
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: isAr ? 800 : 500, lineHeight: 1.2 }}>
            {isAr ? "أدوات ذكاء قانوني لفهم موقفك أولاً" : "AI legal tools that explain your position first"}
          </h1>
          <p className="mt-8 text-lg leading-relaxed" style={{ color: "rgba(245,241,232,0.78)" }}>
            {isAr
              ? "اطرح سؤالك، ارفع مستندك، قدّم قضيتك للتحليل، أو صُغ عقدك. أربع أدوات تعمل على مكتبة قوانين الكويت، وتُحيل إلى نصوص المواد كلما أمكن."
              : "Ask a question, upload a document, submit a full case for analysis, or draft a contract. Four tools running on Kuwait's complete legal library, with references to the article texts wherever possible."}
          </p>
        </div>
      </section>

      {/* TOOLS */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe">
          {needLogin && (
            <div className="luxe-card p-5 mb-10 flex items-center gap-3" style={{ background: "var(--beige)", borderColor: "var(--gold)" }}>
              <ShieldAlert className="w-5 h-5 shrink-0" style={{ color: "var(--gold-deep)" }} />
              <p className={`${isAr ? "font-arabic" : ""} text-sm`} style={{ color: "var(--heading)" }}>
                {isAr
                  ? "لاستخدام أدوات الذكاء القانوني، سجّل الدخول أو أنشئ حساباً جديداً."
                  : "To use the AI tools, sign in or create a free account."}
              </p>
              <div className="ms-auto flex gap-2">
                <Link to="/login"><Button variant="outline" className="rounded-sm h-9 text-sm">{isAr ? "دخول" : "Sign in"}</Button></Link>
                <Link to="/register"><Button className="btn-gold rounded-sm h-9 text-sm">{isAr ? "حساب جديد" : "Register"}</Button></Link>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((tool) => (
              <Link key={tool.testid} to={tool.to} data-testid={tool.testid}
                    className="luxe-card p-7 group">
                <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-5"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <tool.icon className="w-6 h-6" />
                </div>
                <h3 className={`${isAr ? "font-arabic" : "font-display"} text-xl`}
                    style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                  {isAr ? tool.titleAr : tool.titleEn}
                </h3>
                <p className={`mt-3 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`}
                   style={{ color: "var(--ink-soft)" }}>
                  {isAr ? tool.bodyAr : tool.bodyEn}
                </p>
                <span className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium"
                      style={{ color: "var(--gold-deep)" }}>
                  {isAr ? "افتح الأداة" : "Open tool"}
                  {isAr ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe max-w-3xl">
          <div className="luxe-card p-8" style={{ background: "var(--bg)", borderColor: "var(--gold)" }}>
            <div className="flex items-start gap-4">
              <ShieldAlert className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--gold-deep)" }} />
              <div>
                <h3 className={`${isAr ? "font-arabic" : "font-display"} text-lg`}
                    style={{ fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
                  {isAr ? "تنبيه قانوني" : "Legal disclaimer"}
                </h3>
                <p className={`mt-3 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`}
                   style={{ color: "var(--ink-soft)" }}>
                  {isAr
                    ? "الإجابة الصادرة من الذكاء القانوني هي معلومات أولية للمساعدة ولا تُعدّ رأياً قانونياً نهائياً ولا تغني عن استشارة محامٍ مرخص في دولة الكويت."
                    : "Answers from the AI are preliminary informational outputs and are not a final legal opinion. They do not substitute for consultation with a Kuwait-licensed lawyer."}
                </p>
                <p className={`mt-3 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed`}
                   style={{ color: "var(--ink-soft)" }}>
                  <strong>{isAr ? "ملاحظة الخصوصية: " : "Privacy notice: "}</strong>
                  {isAr
                    ? "لا ترفع مستندات شديدة الحساسية إلا من خلال بوابة العميل الآمنة."
                    : "Do not upload highly sensitive documents outside the secure client portal."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y" style={{ background: "var(--navy)", color: "#fff" }}>
        <div className="container-luxe text-center max-w-2xl">
          <h2 className={`${isAr ? "font-arabic" : "font-display"}`}
              style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", fontWeight: isAr ? 800 : 500 }}>
            {isAr ? "هل تحتاج محامياً حقيقياً؟" : "Need a real lawyer?"}
          </h2>
          <p className="mt-5 text-lg" style={{ color: "rgba(245,241,232,0.78)" }}>
            {isAr
              ? "حوّل تحليلك الفوري إلى استشارة كاملة مع محامٍ من فريقنا."
              : "Convert your instant analysis into a full consultation with one of our lawyers."}
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a href={waAi} target="_blank" rel="noopener noreferrer" data-testid="ai-cta-book">
              <Button className="btn-gold rounded-sm px-7 h-12">{isAr ? "احجز استشارة عبر واتساب" : "Book via WhatsApp"}</Button>
            </a>
            <a href={waAi} target="_blank" rel="noopener noreferrer" data-testid="ai-cta-whatsapp">
              <Button variant="outline" className="rounded-sm px-7 h-12 border-white/30 text-white hover:bg-white/10 hover:text-white">
                <Check className="w-4 h-4 me-2" />
                {isAr ? "تواصل واتساب" : "WhatsApp Us"}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
